import React, { useEffect, useState } from 'react';
import { useGetBonusesStatisticsQuery } from 'features/userTransactions/api';
import { CityType } from 'shared/types';
import { AutocompleteFilter, DataAutocompleteFilterType, FilterColumn, FilterDate } from 'shared/ui/Filter';
import debounce from 'lodash.debounce';
import { Dayjs } from 'dayjs';
import { useTransformBonusStatisticsData } from 'widgets/bonusesStatistics/lib';
import { DataTableBackendPagination } from 'shared/ui/Table';
import { useGetCitiesQuery } from 'features/getCities/api/getCitiesApi';
import { useCacheData } from 'shared/hooks';
import { useNavigate } from 'react-router-dom';
import { BASE_URL } from 'shared/conf/constants';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from 'app/store';
import { setCitiesData } from 'features/getCities/model/citiesSlice';
import { SelectFilter } from 'shared/ui/Filter/ui/SelectFilter';
import { AMOUNT_TYPES, OPERATION_TYPES, STATUS_OPTIONS } from 'widgets/bonusesStatistics/const';
import { useGetActivitiesQuery } from 'features/getActivities/api/getActivitiesApi';
import { dateFormatted } from 'shared/helpers/dateFormatted';
import { bonusStatisticsUrlConfig, downloadBonusStatisticsFile } from 'entities/bonusStatistics';
import { colummConfig } from 'widgets/bonusesStatistics/model';
import { BonusesStatisticsItemTableType } from 'entities/bonusStatistics/types';

type FilterBonusStatisticsType = {
  search: string;
  status: string;
  dateAfter: string | null;
  dateBefore: string | null;
  citiesIds: number[];
  activityIds: number[];
  transactionType: string[];
  amount: string;
};

export const BonusesStatisticsWidget = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const citiesStore = useSelector((state: RootState) => state.cities.data);

  const [pagination, setPagination] = useState({
    rowsPerPage: 5,
    page: 0,
  });
  const [searchActivity, setSearchActivity] = useState('');
  const [searchCities, setSearchCities] = useState('');
  const [isLoadingDownloadTable, setIsLoadingDownloadTable] = useState(false);
  const [filterBonusStatistics, setFilterBonusStatistics] = useState<FilterBonusStatisticsType>({
    search: '',
    status: '',
    dateAfter: null,
    dateBefore: null,
    activityIds: [],
    citiesIds: [],
    transactionType: [],
    amount: '',
  });

  const { data: cities, isSuccess: isSuccessCities } = useGetCitiesQuery({
    search: searchCities,
  });

  const { data: activitiesList, isSuccess: isSuccessActivitiesList } = useGetActivitiesQuery({
    page: 1,
    size: 100,
    search: searchActivity,
    activity_type_ids: [],
  });

  const { data: bonusesStatistics, refetch } = useGetBonusesStatisticsQuery({
    size: pagination.rowsPerPage,
    page: pagination.page + 1,
    ...filterBonusStatistics,
  });

  const transformCitiesData = cities?.map(({ id, name_ru }) => ({
    id,
    name: name_ru,
  }));

  const transformActivityData = activitiesList?.items.map(({ id, name }) => ({
    id,
    name: name.ru,
  }));

  const transformOperationTypesData = OPERATION_TYPES.map(({ label, value }) => ({
    id: value,
    name: label,
  }));

  useEffect(() => {
    if (citiesStore.length === 0) {
      dispatch(setCitiesData(cities || []));
    }
  }, [dispatch, cities]);

  useEffect(() => {
    refetch();
  }, [filterBonusStatistics]);

  const [fieldVisibility, setFieldVisibility] = useState(colummConfig.map(() => true));

  const filterField = colummConfig.filter((column, index) => fieldVisibility[index]);

  const onSearch = debounce((search) => {
    setFilterBonusStatistics({ ...filterBonusStatistics, search });
    setPagination({ ...pagination, page: 0 });
  }, 300);

  const handleTableUpdate = () => {
    setPagination({ ...pagination, page: 0 });
    refetch();
  };

  const onRowClick = (id: number | string) => {
    navigate(`/bonuses_statistics/${id}`);
  };

  const handleDateChange = (date: Dayjs | null, dateType: 'dateAfter' | 'dateBefore') => {
    const dateFormat = dateFormatted(date);
    const dateSelect = { [dateType]: dateFormat };
    setFilterBonusStatistics({ ...filterBonusStatistics, ...dateSelect });
  };

  const handleAutocompleteSelectedChange = (
    values: DataAutocompleteFilterType[],
    key: 'activityIds' | 'citiesIds' | 'transactionType',
  ) => {
    const selectedIds = values.map(({ id }) => id);
    setFilterBonusStatistics({ ...filterBonusStatistics, [key]: selectedIds });
  };

  const handleSelectChange = (value: string, key: 'amount' | 'transactionType' | 'status') => {
    setFilterBonusStatistics({ ...filterBonusStatistics, [key]: value });
  };

  const handleTableDownload = () => {
    setIsLoadingDownloadTable(true);

    const url = `${BASE_URL}/wallet/admin/transactions/xlsx/?${bonusStatisticsUrlConfig({
      ...filterBonusStatistics,
      pagination: false,
    })}`;

    downloadBonusStatisticsFile({ url, setIsLoadingDownloadTable });
  };
  const cachedCities = useCacheData<CityType>(citiesStore as CityType[]);

  const transformedData = useTransformBonusStatisticsData(bonusesStatistics, cachedCities);

  return (
    <>
      <div
        style={{
          display: 'flex',
          marginBottom: '10px',
          flexDirection: 'row',
          flexWrap: 'wrap',
          gridGap: '10px',
        }}
      >
        <FilterDate
          label="Дата c"
          handleDateValuesChange={(date) => handleDateChange(date, 'dateAfter')}
        />
        <FilterDate
          label="Дата по"
          handleDateValuesChange={(date) => handleDateChange(date, 'dateBefore')}
        />
        <AutocompleteFilter
          data={transformCitiesData || []}
          label="Выбрать город"
          setSearch={setSearchCities}
          loadingText="По вашему запросу ничего не найдено"
          handleSelectedValuesChange={(values) => handleAutocompleteSelectedChange(values, 'citiesIds')}
          isLoading={isSuccessCities}
          style={{ width: '20%', paddingTop: '8px' }}
        />
        <SelectFilter
          data={AMOUNT_TYPES}
          label="Тип"
          onChange={(value) => handleSelectChange(value as string, 'amount')}
          style={{ marginTop: '8px', width: '10%' }}
        />
        <AutocompleteFilter
          data={transformOperationTypesData || []}
          label="Тип операции"
          loadingText="По вашему запросу ничего не найдено"
          handleSelectedValuesChange={(values) => handleAutocompleteSelectedChange(values, 'transactionType')}
          style={{ marginTop: '8px', width: '20%' }}
        />
        <AutocompleteFilter
          data={transformActivityData || []}
          label="Выбрать акции"
          loadingText="По вашему запросу ничего не найдено"
          setSearch={setSearchActivity}
          handleSelectedValuesChange={(values) => handleAutocompleteSelectedChange(values, 'activityIds')}
          isLoading={isSuccessActivitiesList}
          style={{ width: '29%', paddingTop: '8px' }}
        />
        <SelectFilter
          data={STATUS_OPTIONS}
          label="Статус"
          onChange={(value) => handleSelectChange(value as string, 'status')}
          style={{ marginTop: '8px', width: '15%' }}
        />
      </div>
      <FilterColumn
        columnConfig={colummConfig}
        fieldVisibility={fieldVisibility}
        setFieldVisibility={setFieldVisibility}
        style={{ marginBottom: '10px', justifyContent: 'flex-end' }}
      />
      <DataTableBackendPagination<BonusesStatisticsItemTableType>
        title="Отчет по бонусам"
        data={transformedData}
        total={bonusesStatistics?.total || 0}
        pagination={pagination}
        setPagination={setPagination}
        columnConfig={filterField}
        onRowClick={(id) => onRowClick(id)}
        search
        searchPlaceholder="Поиск по коду двери"
        onSearch={onSearch}
        buttonsController
        isLoadingDownload={isLoadingDownloadTable}
        onUpdateTable={handleTableUpdate}
        onDownloadTable={handleTableDownload}
      />
    </>
  );
};
